import { ArrowCompactDownIcon, ArrowCompactUpIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import { getScoreColorVariant, serializeScore } from '@vizit/utils';
import * as React from 'react';
import { Badge, BadgeLevel, BadgeSize } from '../badge';
import { Skeleton } from '../skeleton';
import { Text, TextColor, TextVariant } from '../text';
import { scoreTextClasses, stickerClasses } from './styles';
import type { ScoreStickerV2Props } from './types';

/**
 * Score sticker v2 displays a score with a color-coded flat background.
 * Uses lighter 200-level background colors and monospace bold text.
 * Optionally shows a score diff indicator at the bottom.
 *
 * @example
 * <ScoreStickerV2 score={38} scoreDiff={11} />
 */
const ScoreStickerV2 = React.forwardRef<HTMLDivElement, ScoreStickerV2Props>((props, forwardedRef) => {
  const { score, scoreDiff, isLoading = false, className, ...other } = props;

  const scoreLabel = serializeScore(score);
  const colorVariant = getScoreColorVariant(score);
  const hasDiff = scoreDiff != null && scoreDiff !== 0;
  const isPositive = hasDiff && scoreDiff > 0;

  if (isLoading) {
    return (
      <div ref={forwardedRef} {...other}>
        <Skeleton className={cx('h-64 w-64', className)} variant="square" />
      </div>
    );
  }

  return (
    <div
      ref={forwardedRef}
      className={cx(stickerClasses({ colorVariant }), className)}
      aria-label={`Score: ${scoreLabel}`}
      data-testid="score-sticker-v2"
      {...other}
    >
      <Text
        variant={TextVariant.MonoXxl}
        elementType="span"
        color={TextColor.Inherit}
        className={scoreTextClasses({ colorVariant })}
        showEllipsis={false}
      >
        {scoreLabel}
      </Text>
      {hasDiff && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2">
          <Badge
            level={isPositive ? BadgeLevel.Success : BadgeLevel.Error}
            size={BadgeSize.Xs}
            bordered={false}
            filled={false}
          >
            {isPositive ? <ArrowCompactUpIcon size={8} /> : <ArrowCompactDownIcon size={8} />}
            {Math.abs(scoreDiff)}
          </Badge>
        </div>
      )}
    </div>
  );
});

ScoreStickerV2.displayName = 'ScoreStickerV2';

export { ScoreStickerV2 };
export type { ScoreStickerV2Props };
